import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermissions } from '@/features/auth';
import type { BreadcrumbItem } from '@shared/components';
import type { FundAccountSelection, PairPeriodStatement, StatementPeriod } from '../types/dealerFundStatementPeriod.types';
import { cardState, type FundAccountCardState } from '../utils/fundAccountCardState';
import {
    dealerFinancialsTabPath,
    parsePeriodSearch,
    periodSearchParams,
} from '../utils/fundAccountStatementRoute';
import {
    describePeriod,
    formatStatementDates,
    fundAccountCardTitle,
    fundAccountSubtitle,
    productLabel,
} from '../utils/fundStatementLabels';
import { groupStatementBlocks, type StatementLayout } from '../utils/statementBlocks';
import { periodKey, utcTodayIsoDate } from '../utils/statementPeriod';
import { useDealerQuery } from './useDealerQuery';
import type { PeriodHeaderModel } from './useFundStatementPeriodSection';
import { usePairPeriodStatementQuery } from './usePairPeriodStatementQuery';
import { useOpenStatementLines, type OpenStatementLines } from './useOpenStatementLines';
import { useProductNames } from './useProductNames';
import { useStatementPeriod, type StatementPeriodMode } from './useStatementPeriod';

const FALLBACK_TITLE = 'Fund Statement';
const FALLBACK_DEALER_NAME = 'Dealer';

export interface FundAccountStatementPageModel {
    header: PeriodHeaderModel;
    breadcrumbs: BreadcrumbItem[];
    title: string;
    subtitle: string;
    state: FundAccountCardState;
    periodLabel: string;
    datesLine: string | null;
    statement: PairPeriodStatement | null;
    selection: FundAccountSelection;
    period: StatementPeriod;
    todayIso: string;
    lines: OpenStatementLines;
    layout: StatementLayout | null;
    onRetry: () => void;
}

export function useFundAccountStatementPage(
    dealerId: string,
    sellingDealerId: string,
    productId: string,
): FundAccountStatementPageModel {
    const { hasPermission } = usePermissions();
    const canChooseRange = hasPermission({ resource: 'ledger', action: 'fund-statement.date-range' });
    const [searchParams, setSearchParams] = useSearchParams();
    const [initialPeriod] = useState<StatementPeriod | null>(() =>
        parsePeriodSearch(searchParams, utcTodayIsoDate(new Date()), canChooseRange),
    );
    const state = useStatementPeriod(canChooseRange, undefined, initialPeriod);
    const selection = useMemo(() => ({ sellingDealerId, productId }), [sellingDealerId, productId]);
    const lines = useOpenStatementLines(periodKey(state.period));
    const query = usePairPeriodStatementQuery(selection, state.period, state.todayIso);
    const dealer = useDealerQuery(dealerId, { suppressForbiddenToast: true }).data;
    const productNames = useProductNames(true);

    const result = query.data;
    const statement = result?.status === 'ok' ? result.statement : null;
    const layout = useMemo(() => (statement ? groupStatementBlocks(statement.rows) : null), [statement]);
    const { refetch } = query;
    const onRetry = useCallback(() => { void refetch({ cancelRefetch: false }); }, [refetch]);

    const writePeriod = useCallback(
        (period: StatementPeriod) => setSearchParams(periodSearchParams(period), { replace: true }),
        [setSearchParams],
    );
    const { setMode, selectMonth, applyRange, month, rangeDraft, rangeError } = state;
    const onModeChange = useCallback((mode: StatementPeriodMode) => {
        setMode(mode);
        if (mode === 'month') writePeriod({ kind: 'month', month });
    }, [setMode, writePeriod, month]);
    const onMonthChange = useCallback((next: string) => {
        selectMonth(next);
        writePeriod({ kind: 'month', month: next });
    }, [selectMonth, writePeriod]);
    const onApplyRange = useCallback(() => {
        if (rangeError !== null || !rangeDraft.from || !rangeDraft.to) return;
        applyRange();
        writePeriod({ kind: 'range', from: rangeDraft.from, to: rangeDraft.to });
    }, [applyRange, writePeriod, rangeError, rangeDraft]);

    const label = productLabel(productId, productNames, statement?.sageAccountName ?? null);
    const title = statement
        ? fundAccountCardTitle({
            sageAccountCode: statement.sageAccountCode,
            dealerCode: statement.dealerCode,
            sellingDealerId,
            productLabel: label,
        })
        : FALLBACK_TITLE;

    return {
        header: {
            mode: state.mode,
            canChooseRange,
            month,
            monthOptions: state.monthOptions,
            rangeDraft,
            rangeError,
            todayIso: state.todayIso,
            onModeChange,
            onMonthChange,
            onRangeDraftChange: state.setRangeDraft,
            onApplyRange,
        },
        breadcrumbs: [
            { label: 'Dealers', to: '/admin/dealers' },
            { label: dealer?.name ?? FALLBACK_DEALER_NAME, to: `/admin/dealers/${dealerId}` },
            { label: 'Financials', to: dealerFinancialsTabPath(dealerId) },
            { label: title },
        ],
        title,
        subtitle: fundAccountSubtitle(statement?.dealerCode ?? null, sellingDealerId, label),
        state: cardState(true, query.isLoading, query.isError, result?.status),
        periodLabel: describePeriod(state.period, state.todayIso),
        datesLine: statement ? formatStatementDates(statement.from, statement.to) : null,
        statement,
        selection,
        period: state.period,
        todayIso: state.todayIso,
        lines,
        layout,
        onRetry,
    };
}
